import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ArrowRight, CircleNotch, Lightning } from "@phosphor-icons/react";
import { PILLARS, editorPath } from "@/lib/tools";

export default function TzuConcierge() {
  const navigate = useNavigate();
  const [problem, setProblem] = useState("");
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState(null);
  const [launching, setLaunching] = useState(null);

  const route = async () => {
    const text = problem.trim();
    if (!text) return;
    setLoading(true);
    setRecs(null);
    try {
      const res = await api.post("/tzu/route", { problem: text });
      setRecs(res.data.recommendations || []);
      if (!res.data.recommendations?.length) toast.info("No clear match — pick a tool below.");
    } catch {
      toast.error("Concierge is unavailable right now");
    } finally {
      setLoading(false);
    }
  };

  const launch = async (toolType) => {
    setLaunching(toolType);
    try {
      const res = await api.post("/documents", { tool_type: toolType, context: problem.trim() });
      navigate(editorPath(res.data));
    } catch {
      toast.error("Could not launch the wizard");
      setLaunching(null);
    }
  };

  return (
    <section
      data-testid="tzu-concierge"
      className="rounded-sm border border-emerald-700 bg-white shadow-sm p-5 sm:p-6"
    >
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-sm bg-emerald-50 border border-emerald-200 flex items-center justify-center">
          <Sparkle size={20} weight="duotone" className="text-emerald-700" />
        </span>
        <div>
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-700">Tzu Concierge</p>
          <h2 className="text-lg font-semibold text-slate-900">What problem are you trying to solve?</h2>
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <textarea
          data-testid="concierge-input"
          value={problem}
          onChange={(e) => setProblem(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) route(); }}
          rows={2}
          placeholder="e.g. Our biggest plant keeps missing delivery dates and I don't know where the real bottleneck is…"
          className="flex-1 rounded-sm border border-slate-300 bg-white px-3.5 py-2.5 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent resize-none"
        />
        <button
          data-testid="concierge-submit"
          onClick={route}
          disabled={loading || !problem.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-sm bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 transition-colors disabled:opacity-50 shrink-0 self-start"
        >
          {loading ? <><CircleNotch size={16} className="animate-spin" /> Routing…</> : <><Lightning size={16} weight="fill" /> Route me</>}
        </button>
      </div>

      {recs && recs.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="concierge-recommendations">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Recommended framework{recs.length > 1 ? "s" : ""}</p>
          {recs.map((r) => {
            const pillar = PILLARS[r.pillar] || PILLARS.OPERATIONS;
            return (
              <div
                key={r.tool_type}
                data-testid={`concierge-rec-${r.tool_type}`}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-sm border border-slate-200 bg-slate-50 p-4 ${pillar.ring} transition-colors`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-slate-900">{r.tool_name}</h3>
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${pillar.chip}`}>{pillar.label}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{r.reason}</p>
                </div>
                <button
                  data-testid={`concierge-launch-${r.tool_type}`}
                  onClick={() => launch(r.tool_type)}
                  disabled={launching === r.tool_type}
                  className="inline-flex items-center justify-center gap-2 rounded-sm bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50 shrink-0"
                >
                  {launching === r.tool_type ? (
                    <><CircleNotch size={16} className="animate-spin" /> Launching…</>
                  ) : (
                    <>Launch {r.tool_name} Wizard <ArrowRight size={14} /></>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
